import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { checkSnapshotAuthenticity, fetchAuthenticityChecks } from "@/lib/api";

/** Prefix shared by every Jev query, so one invalidation refreshes badges and the recent list. */
export const JEV_CHECKS_KEY = ["snapshot-authenticity-checks"] as const;
/** Bulk checks are paid model calls; cap one click at this many. */
export const JEV_BULK_MAX = 25;
const BULK_CONCURRENCY = 4;

/** Latest Jev check per visible snapshot, plus a capped, concurrency-limited "check these" action. */
export function useJevChecks(snapshotIds: string[]) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pending, setPending] = useState<Set<string>>(new Set());

  const checksQuery = useQuery({
    queryKey: [...JEV_CHECKS_KEY, "latest", snapshotIds],
    queryFn: () => fetchAuthenticityChecks(snapshotIds),
    enabled: snapshotIds.length > 0,
  });

  const checkMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const batch = [...new Set(ids)].slice(0, JEV_BULK_MAX);
      setPending((prev) => new Set([...prev, ...batch]));
      const failures: string[] = [];
      let next = 0;
      const worker = async () => {
        while (next < batch.length) {
          const id = batch[next++];
          try {
            const { outcomes } = await checkSnapshotAuthenticity(id);
            // The function stores a row even when the model call fails; surface that too.
            const failed = outcomes.find((o) => o.error);
            if (failed?.error) failures.push(failed.error);
          } catch (err) {
            failures.push(err instanceof Error ? err.message : String(err));
          } finally {
            setPending((prev) => {
              const rest = new Set(prev);
              rest.delete(id);
              return rest;
            });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, batch.length) }, worker));
      return { checked: batch.length, failures };
    },
    onSuccess: ({ checked, failures }) => {
      toast({
        title: failures.length
          ? `Jev: ${failures.length} of ${checked} checks failed`
          : `Jev checked ${checked}`,
        description: failures[0],
        variant: failures.length === checked ? "destructive" : undefined,
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: JEV_CHECKS_KEY }),
  });

  return {
    checks: checksQuery.data ?? {},
    checksError: checksQuery.error,
    isLoadingChecks: checksQuery.isLoading && snapshotIds.length > 0,
    pending,
    isChecking: checkMutation.isPending,
    check: checkMutation.mutate,
  };
}
